import { useMemo, type JSX } from 'react';
import { useStore } from '@/state/store';
import { Panel } from '@/components/ui/Panel';
import {
  angleBisectors,
  clipLineToPolygon,
  lineThroughPoints,
  perpendicularBisector,
  perpendicularThrough,
  parallelThrough,
  reflectPoint,
  type Line,
} from '@/origami/constructions';
import { sub2 } from '@/geometry/vec2';
import type { CreaseAssignment } from '@/origami/types';

/**
 * Construction tools. Each one turns the current selection into one or more
 * candidate lines, clips them to the paper outline and offers them as creases.
 *
 * Multi-solution constructions (angle bisectors) list every valid answer instead
 * of picking one, which is the same discipline the Huzita-Hatori axioms will
 * need when they land.
 */
export function ConstructPanel(): JSX.Element {
  const pattern = useStore((s) => s.pattern);
  const analysis = useStore((s) => s.analysis);
  const selection = useStore((s) => s.selection);
  const addCrease = useStore((s) => s.addCrease);
  const tool = useStore((s) => s.tool);

  const vertices = selection.filter((s) => s.kind === 'vertex').map((s) => s.index);
  const edges = selection.filter((s) => s.kind === 'edge').map((s) => s.index);

  /** The paper outline, taken from the outer cycle of the planar graph. */
  const outline = useMemo(() => {
    const cycles = analysis.topology.planar.outerCycles;
    if (cycles.length === 0) return [];
    let best = cycles[0];
    for (const c of cycles) if (c.length > best.length) best = c;
    return best.map((v) => pattern.verticesCoords[v]);
  }, [analysis, pattern]);

  const candidates: Line[] = [];
  if (vertices.length === 2) {
    const a = pattern.verticesCoords[vertices[0]];
    const b = pattern.verticesCoords[vertices[1]];
    const through = lineThroughPoints(a, b);
    if (through) candidates.push(through);
    const bisector = perpendicularBisector(a, b);
    if (bisector) candidates.push(bisector);
  }
  if (edges.length === 1 && vertices.length === 1) {
    const [ea, eb] = pattern.edgesVertices[edges[0]];
    const dir = sub2(pattern.verticesCoords[eb], pattern.verticesCoords[ea]);
    const point = pattern.verticesCoords[vertices[0]];
    const par = parallelThrough(point, dir);
    const perp = perpendicularThrough(point, dir);
    if (par) candidates.push({ ...par, label: 'parallel through vertex' });
    if (perp) candidates.push({ ...perp, label: 'perpendicular through vertex' });
  }
  if (edges.length === 2) {
    const [a1, a2] = pattern.edgesVertices[edges[0]];
    const [b1, b2] = pattern.edgesVertices[edges[1]];
    const shared = [a1, a2].find((v) => v === b1 || v === b2);
    if (shared !== undefined) {
      const other = (pair: [number, number]): number => (pair[0] === shared ? pair[1] : pair[0]);
      const dirA = sub2(
        pattern.verticesCoords[other(pattern.edgesVertices[edges[0]])],
        pattern.verticesCoords[shared],
      );
      const dirB = sub2(
        pattern.verticesCoords[other(pattern.edgesVertices[edges[1]])],
        pattern.verticesCoords[shared],
      );
      candidates.push(...angleBisectors(pattern.verticesCoords[shared], dirA, dirB));
    }
  }
  if (edges.length === 1 && vertices.length === 1) {
    const [ea, eb] = pattern.edgesVertices[edges[0]];
    const mirror: Line = {
      point: pattern.verticesCoords[ea],
      direction: sub2(pattern.verticesCoords[eb], pattern.verticesCoords[ea]),
      label: 'mirror',
    };
    const image = reflectPoint(pattern.verticesCoords[vertices[0]], mirror);
    const reflected = lineThroughPoints(pattern.verticesCoords[vertices[0]], image);
    if (reflected) {
      candidates.push({ ...reflected, label: 'reflection line (point → image)' });
    }
  }

  const assignment: CreaseAssignment =
    tool === 'mountain' ? 'M' : tool === 'valley' ? 'V' : tool === 'cut' ? 'C' : 'U';

  return (
    <Panel title="Constructions" defaultOpen={false}>
      <p className="note" style={{ marginTop: 0 }}>
        Select geometry in the 2D editor, then build a crease from it. Two vertices give a line and
        its perpendicular bisector; two creases sharing a vertex give both angle bisectors; a crease
        plus a vertex gives a parallel, a perpendicular and a reflection.
      </p>
      {candidates.length === 0 ? (
        <p className="note faint">
          Nothing constructible from the current selection ({vertices.length} vertex/vertices,{' '}
          {edges.length} crease(s)).
        </p>
      ) : (
        candidates.map((line, i) => {
          const pieces = clipLineToPolygon(line, outline);
          return (
            <button
              key={`${line.label}-${i}`}
              className="issue info"
              disabled={pieces.length === 0}
              onClick={() => {
                for (const [a, b] of pieces) addCrease(a, b, assignment);
              }}
            >
              <span className="t">{line.label}</span>
              <span className="d">
                {pieces.length === 0
                  ? 'does not cross the paper'
                  : `adds ${pieces.length} crease${pieces.length > 1 ? 's' : ''} as ${assignment}`}
              </span>
            </button>
          );
        })
      )}
    </Panel>
  );
}
